import {
  Grid,
  styled,
  Tab,
  Tabs,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import authActions from "../../redux/reducers/auth/actions";
import About from "../../components/About";
import EditProfile from "../EditProfile";
import ChangePassword from "../ChangePassword";
import PhoneVerificationModal from "../../components/PhoneVerificationModal";
import TwoFectorModal from "../../components/TwoFectorModal";
import NotificationSettings from "../NotificationSettings";
import Security from "../../components/Security";
import DocumentsManagement from "../../components/DocumentsManagement";
import Portfolio from "../../components/Portfolio";
import useStyles from "./styles";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";
import WelcomeBanner from "../EditProfile/components/WelcomeBanner";
import { useCRUDPermission } from "../../components/hooks/usePermission";
import Permission from "../../components/Permission";

const StyledHeading = styled("h2")(({ theme }) => ({
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  fontSize: "24px",
  fontWeight: "500",
  margin: 0,
  [theme.breakpoints.down("md")]: {
    fontSize: "20px",
  },
}));

const StyledTab = styled(Tab)(({ theme, selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0d0d0d" : "#fff",
  border: "1px solid #d7d8d9",
  borderRadius: "16px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "10px",
  padding: "14px 8px !important",
  fontFamily: "Inter Tight",
  [theme.breakpoints.down("md")]: {
    // Pills are the only horizontal-scroll affordance on mobile, so they need
    // to stay tappable (>=36px) while fitting more than one per screen width.
    fontSize: "13px",
    minHeight: "36px !important",
    minWidth: "auto",
    padding: "8px 14px !important",
    marginRight: "8px",
    whiteSpace: "nowrap",
  },
  "&:hover": {
    backgroundColor: selected ? "#0d0d0d" : "",
    color: !selected && "#000",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent !important",
  },
}));

export default function AccountSettings() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setAccountTab } = authActions;
  const { userData, accountTab } = useSelector((state) => state.auth);
  const { isWelcomeBannerDismissed } = useSelector((state) => state.common);
  const [tabValue, setTabValue] = useState(accountTab);
  const [showModal, setShowModal] = useState(false);
  const [show2FModal, setShow2FModal] = useState(false);

  // Token validation hook
  const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

  // Check PROFILE permissions
  const { hasPermission: hasProfileRead } = useCRUDPermission(
    "PROFILE",
    "READ",
  );
  const { hasPermission: hasProfileUpdate } = useCRUDPermission(
    "PROFILE",
    "UPDATE",
  );
  const { hasPermission: hasProfileCreate } = useCRUDPermission(
    "PROFILE",
    "CREATE",
  );
  const { hasPermission: hasProfileDelete } = useCRUDPermission(
    "PROFILE",
    "DELETE",
  );

  // Check USERS permissions
  const { hasPermission: hasUsersRead } = useCRUDPermission("USERS", "READ");

  // User can edit if they have any of: CREATE, UPDATE, or DELETE permissions
  const canEdit = hasProfileUpdate || hasProfileCreate || hasProfileDelete;

  // Security tab should only be visible to base "contractor" role
  const isBaseContractor = userData?.role?.toLowerCase() === "contractor";

  useEffect(() => {
    setTabValue(accountTab);
  }, [accountTab]);

  // Redirect if user doesn't have PROFILE READ permission
  useEffect(() => {
    if (!hasProfileRead && userData?.role) {
      navigate("/dashboard");
    }
  }, [hasProfileRead, userData, navigate]);

  // Reset tab if non-contractor tries to access Security tab (tabValue === 5)
  useEffect(() => {
    if (tabValue === 5 && !isBaseContractor) {
      setTabValue(0);
      dispatch(setAccountTab(0));
    }
  }, [tabValue, isBaseContractor, dispatch]);

  // The scroll panel below the tabs is reused across tabs, so it keeps the
  // previous tab's offset unless we send it back to the top on every switch.
  const contentScrollRef = useRef(null);

  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, [tabValue]);

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "6px",
          fontFamily: "Inter Tight",
          // The page owns its scrolling instead of deferring to the app shell:
          // everything down to the tab strip is pinned, and each tab's content
          // scrolls inside the panel below it.
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          // Pinning ~250-320px of chrome needs vertical room to spare. On a
          // short viewport (landscape phone) it would starve the panel, so
          // below this height the page reverts to one continuous scroll.
          "@media (max-height: 640px)": {
            height: "auto",
            overflow: "visible",
          },
        }}
      >
        {/* Pinned chrome — page heading, profile summary and tab strip */}
        <Box sx={{ flexShrink: 0 }}>
          {/* Page heading */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: { xs: "16px 16px 12px", md: "4vh 2vw 2vh 2vw" },
              background:
                "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          >
            <StyledHeading>Account Settings</StyledHeading>
          </Box>
          {/* Profile Header - Always show */}
          <Box
            sx={{
              padding: { xs: "0 16px", md: "0 2vw" },
              marginBottom: { xs: "16px", md: "2vh" },
            }}
          >
            <div
              style={{ marginBottom: "1vh", borderRadius: 2, border: "none", boxShadow: "none", padding: "0 !important" }}
            >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "center" },
                    justifyContent: "space-between",
                    gap: { xs: 2.5, md: 0 },
                  }}
                >
                  {/* Left Section - Avatar and Info */}
                  <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                    <Avatar
                      src={userData?.profile_url}
                      sx={{
                        width: { xs: 56, md: 70 },
                        height: { xs: 56, md: 70 },
                        mr: { xs: 2, md: 3 },
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 500,
                          mb: 0.5,
                          color: "#949DAA",
                          fontSize: { xs: "14px", md: "18px" },
                        }}
                      >
                        {userData?.contractor_data?.company_name ||
                          "CS Interior"}
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 500,
                          mb: 0,
                          color: "#000",
                          fontSize: { xs: "18px", md: "24px" },
                        }}
                      >
                        Welcome, {userData?.username || "User"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
                        {userData?.role === "contractor"
                          ? "Contractor"
                          : userData?.role === "designer"
                            ? "Designer"
                            : "Supplier"}{" "}
                        Member since{" "}
                        {new Date().toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right Section - Business Info and Button.
                      On mobile the two stats sit side by side under the
                      identity block and the CTA spans the full width, so the
                      three items never share one cramped row. */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      alignItems: { xs: "stretch", md: "center" },
                      gap: { xs: 2, md: 3 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 4, md: 3 },
                      }}
                    >
                      {/* Employees Section */}
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#949DAA",
                            fontWeight: 400,
                            fontSize: { xs: "12px", md: "14px" },
                            mb: 0.5,
                          }}
                        >
                          Employees
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 500,
                            color: "#000",
                            fontSize: { xs: "16px", md: "20px" },
                          }}
                        >
                          {userData?.contractor_data?.company_size
                            ? userData.contractor_data.company_size.replace(
                                /\s+(employees?|employee)$/i,
                                "",
                              )
                            : "11-50"}
                        </Typography>
                      </Box>

                      {/* Years in Business Section */}
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#949DAA",
                            fontWeight: 400,
                            fontSize: { xs: "12px", md: "14px" },
                            mb: 0.5,
                          }}
                        >
                          Years in business
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 500,
                            color: "#000",
                            fontSize: { xs: "16px", md: "20px" },
                          }}
                        >
                          {userData?.contractor_data?.total_years_in_business
                            ? userData.contractor_data.total_years_in_business.replace(
                                /\s+(years?|year)$/i,
                                "",
                              )
                            : "4-7"}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Edit Profile Button - Only show if user has UPDATE permission */}
                    <Permission resource="PROFILE" action="UPDATE">
                      <Button
                        variant="contained"
                        fullWidth={isMobile}
                        startIcon={<Edit />}
                        onClick={() => navigate("/edit-profile-form")}
                        sx={{
                          backgroundColor: "#4D6BF6",
                          borderRadius: "10px",
                          textTransform: "none",
                          px: 2,
                          py: 1.5,
                          height: "fit-content",
                          ml: { xs: 0, md: 6 },
                          boxShadow: "none",
                        }}
                      >
                        Edit profile
                      </Button>
                    </Permission>
                  </Box>
                </Box>
            </div>
          </Box>

          {/* Navigation Tabs - Only show if user has PROFILE READ permission */}
          {hasProfileRead && (
            <Box
              sx={{
                padding: { xs: "0 16px", md: "0 2vw" },
                marginBottom: { xs: "16px", md: "2vh" },
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(v, b) => {
                  if (b === 6) {
                    // Only navigate to Users tab if user has USERS READ permission
                    if (hasUsersRead) {
                      navigate("/account-setting/users");
                    }
                  } else {
                    setTabValue(b);
                    dispatch(setAccountTab(b));
                  }
                }}
                variant="scrollable"
                scrollButtons={isMobile ? false : "auto"}
                sx={{
                  minHeight: { xs: 36, md: 48 },
                  "& .MuiTabs-indicator": {
                    background: "none",
                  },
                  // Let the pill strip bleed to the screen edges on mobile so
                  // the last tab isn't clipped mid-pill, and hide the
                  // scrollbar the swipe replaces.
                  "& .MuiTabs-scroller": {
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  },
                  "& .MuiTabs-flexContainer": {
                    gap: 0,
                    paddingRight: { xs: "8px", md: 0 },
                  },
                }}
              >
                {/* Every tab sets an explicit `value`. Without it MUI numbers
                    them by their position among the rendered children, so the
                    hidden Notifications tab (index 3) shifted everything after
                    it down by one: Change Password reported 3 while the panel
                    below looked for 4 (blank screen), Security reported 4 and
                    opened Change Password, Users reported 5 and opened
                    Security. Explicit values also keep the numbering stable
                    when a tab is conditionally hidden. */}
                <StyledTab label="Profile" value={0} selected={tabValue === 0} />
                <StyledTab
                  label="Documents"
                  value={1}
                  selected={tabValue === 1}
                />
                <StyledTab
                  label="Portfolio"
                  value={2}
                  selected={tabValue === 2}
                />
                {/* Notifications tab hidden — value 3 stays reserved */}
                {/* <StyledTab label="Notifications" value={3} selected={tabValue === 3} /> */}
                {userData?.social_connection === "google" ||
                userData?.social_connection === "fb" ||
                userData?.social_connection === "apple" ? null : (
                  <StyledTab
                    label="Change Password"
                    value={4}
                    selected={tabValue === 4}
                  />
                )}
                {/* Security tab - Only visible to base contractor role */}
                {isBaseContractor && (
                  <StyledTab
                    label="Security"
                    value={5}
                    selected={tabValue === 5}
                  />
                )}
                {hasUsersRead && (
                  <StyledTab label="Users" value={6} selected={tabValue === 6} />
                )}
              </Tabs>
            </Box>
          )}
        </Box>

        {/* Scrollable panel — one independent scroll region per tab */}
        <Box
          ref={contentScrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            "@media (max-height: 640px)": {
              flex: "none",
              overflowY: "visible",
            },
          }}
        >
          <Box sx={{ padding: { xs: "0 16px", md: "0 2vw" } }}>
            {!isWelcomeBannerDismissed && <WelcomeBanner />}
          </Box>

          {/* Tab Content - Only render if user has PROFILE READ permission */}
          {hasProfileRead && (
            <>
              {tabValue === 0 ? (
                <EditProfile onTabChange={setTabValue} readOnly={!canEdit} />
              ) : null}
              {tabValue === 1 ? (
                <DocumentsManagement readOnly={!canEdit} />
              ) : null}
              {tabValue === 2 ? <Portfolio readOnly={!canEdit} /> : null}
              {/* Notifications content hidden */}
              {/* {tabValue === 3 ? <NotificationSettings /> : null} */}
              {tabValue === 4 ? (
                userData?.social_connection === "google" ||
                userData?.social_connection === "fb" ||
                userData?.social_connection === "apple" ? (
                  <Security />
                ) : (
                  <ChangePassword />
                )
              ) : null}
              {/* Security tab content - Only visible to base contractor role */}
              {tabValue === 5 && isBaseContractor ? <Security /> : null}
            </>
          )}
        </Box>
      </Box>

      {/* <About /> */}
      <PhoneVerificationModal
        visible={showModal}
        handleClose={() => setShowModal(false)}
      />
      <TwoFectorModal
        visible={show2FModal}
        handleClose={() => setShow2FModal(false)}
      />

      {/* Security Policy Modal */}
      <SecurityPolicyModal
        open={showSecurityModal}
        onConfirm={handleSecurityModalClose}
      />
    </>
  );
}
